import React from "react";
import {
  Scale,
  FileText,
  Gavel,
  ChevronRight,
  LogOut,
  User as UserIcon,
} from "lucide-react";
import { useNav } from "@/context/NavContext";
import { useAuth } from "@/context/AuthContext";

const PLAYFAIR = "'Playfair Display', Georgia, serif";
const INTER = "'Inter', system-ui, -apple-system, sans-serif";
const GOLD = "#B08A45";
const NAVY = "#0F172A";

function CategoryCard({ title, subtitle, Icon, accent, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="group relative w-full overflow-hidden rounded-3xl bg-white p-6 text-left transition-all duration-500 hover:-translate-y-1 focus:outline-none focus-visible:ring-2"
      style={{
        boxShadow:
          "0 20px 45px -25px rgba(15,23,42,0.20), inset 0 0 0 1px rgba(15,23,42,0.05)",
        fontFamily: INTER,
      }}
    >
      {/* accent ribbon */}
      <span
        className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full opacity-70 blur-2xl transition-transform duration-500 group-hover:scale-110"
        aria-hidden="true"
        style={{ background: accent }}
      />
      <div className="relative flex items-start justify-between">
        <div
          className="flex h-14 w-14 items-center justify-center rounded-2xl"
          style={{
            background: `linear-gradient(135deg, ${accent} 0%, rgba(255,255,255,0) 130%)`,
            boxShadow: "inset 0 0 0 1px rgba(15,23,42,0.06)",
          }}
        >
          <Icon className="h-7 w-7" style={{ color: NAVY }} />
        </div>
        <ChevronRight
          className="h-5 w-5 text-stone-400 transition-transform duration-500 group-hover:translate-x-1 group-hover:text-stone-700"
        />
      </div>
      <div className="relative mt-6">
        <h3
          className="text-[22px] leading-tight text-stone-900"
          style={{ fontFamily: PLAYFAIR, letterSpacing: "-0.01em" }}
        >
          {title}
        </h3>
        <p className="mt-1 text-[13.5px] leading-relaxed text-stone-600">
          {subtitle}
        </p>
      </div>
    </button>
  );
}

export default function HomePage() {
  const { navigate } = useNav();
  const { user, logout } = useAuth();

  return (
    <div
      className="min-h-screen w-full"
      style={{ background: "#FAF8F4", fontFamily: INTER }}
      data-testid="home-page"
    >
      {/* Top bar */}
      <header className="sticky top-0 z-20 w-full border-b border-stone-200/80 bg-white/70 backdrop-blur-xl">
        <div className="mx-auto flex h-14 max-w-5xl items-center justify-between px-4">
          <div className="flex items-center gap-2.5">
            <div
              className="flex h-8 w-8 items-center justify-center rounded-lg"
              style={{
                background: "linear-gradient(135deg, #C89A4E, #8a6a2f)",
              }}
            >
              <Scale className="h-4 w-4 text-white" />
            </div>
            <div>
              <div
                className="text-[13px] font-semibold tracking-wide text-stone-900"
                style={{ fontFamily: INTER }}
              >
                Advocate Studio
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {user?.picture ? (
              <img
                src={user.picture}
                alt=""
                className="h-8 w-8 rounded-full ring-1 ring-stone-200"
              />
            ) : (
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-stone-100">
                <UserIcon className="h-4 w-4 text-stone-500" />
              </div>
            )}
            <button
              type="button"
              onClick={logout}
              data-testid="btn-home-logout"
              className="flex h-9 items-center gap-1.5 rounded-full px-3 text-[12.5px] text-stone-600 hover:bg-stone-100"
              title="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="mx-auto max-w-5xl px-4 pt-8 pb-4 sm:pt-12">
        <p
          className="text-[12px] font-semibold uppercase tracking-[0.22em]"
          style={{ color: GOLD }}
        >
          Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
        </p>
        <h1
          className="mt-2 text-stone-900"
          style={{
            fontFamily: PLAYFAIR,
            fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)",
            lineHeight: 1.1,
            letterSpacing: "-0.01em",
          }}
        >
          Choose a document category
        </h1>
        <p className="mt-2 max-w-2xl text-[14px] leading-relaxed text-stone-600">
          Pick a category to get started. Every document opens as a court-ready
          template that you can save and download.
        </p>
      </div>

      {/* Category cards */}
      <div className="mx-auto grid max-w-5xl grid-cols-1 gap-5 px-4 pb-14 pt-4 sm:grid-cols-2 sm:gap-6">
        <CategoryCard
          title="Civil Documents"
          subtitle="Vakalatnama, Adjournment Application, Address Memo — English & Marathi."
          Icon={FileText}
          accent="rgba(176,138,69,0.22)"
          onClick={() => navigate("/civil")}
          testId="card-civil-documents"
        />
        <CategoryCard
          title="Criminal Documents"
          subtitle="Bail Affidavit and more — generated from original court templates."
          Icon={Gavel}
          accent="rgba(15,23,42,0.10)"
          onClick={() => navigate("/criminal")}
          testId="card-criminal-documents"
        />
      </div>
    </div>
  );
}
